import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, Users, GraduationCap, Plus, Wand2, BookOpenCheck, Play, ArrowRight, Award, Filter, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["All", "Beginner", "Intermediate", "Advanced"];
const SORTS = [
  { value: "popular", label: "Most enrolled" },
  { value: "newest", label: "Newest" },
  { value: "duration_asc", label: "Shortest first" },
  { value: "duration_desc", label: "Longest first" },
  { value: "price_asc", label: "Price · low to high" },
  { value: "price_desc", label: "Price · high to low" },
];

export default function Courses() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "browse";
  const isAuthor = ["mentor", "tutor", "admin"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [level, setLevel] = useState("All");
  const [q, setQ] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState("popular");
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (level !== "All") params.level = level;
    const r = await api.get("/courses", { params });
    setItems(r.data);
    setLoading(false);
  };
  const fetchMine = async () => {
    if (!isAuthor) return;
    try { const r = await api.get("/courses/mine"); setMine(r.data); } catch { /* ignore */ }
  };
  const fetchEnrollments = async () => {
    try { const r = await api.get("/enrollments"); setEnrollments(r.data); } catch { /* ignore */ }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [level]);
  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);
  useEffect(() => { fetchMine(); fetchEnrollments(); /* eslint-disable-next-line */ }, [user?.user_id]);

  // Build enrolled list (cross-reference enrollments with course data)
  const enrolledCourses = useMemo(() => {
    if (tab === "mine") return [];
    const byId = Object.fromEntries(items.map(c => [c.id, c]));
    return enrollments
      .map(e => ({ ...byId[e.course_id], _enrollment: e }))
      .filter(c => c && c.id);
  }, [enrollments, items, tab]);
  const enrolledIds = useMemo(() => new Set(enrolledCourses.map(c => c.id)), [enrolledCourses]);

  // All distinct tags from current list
  const allTags = useMemo(() => {
    const list = tab === "mine" ? mine : items;
    const counts = {};
    list.forEach(c => (c.tags || []).forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 20);
  }, [items, mine, tab]);

  // Filtered + sorted browse list (exclude already-enrolled from main grid to surface above)
  const visibleList = useMemo(() => {
    let list = (tab === "mine" ? mine : items.filter(c => !enrolledIds.has(c.id))).slice();
    if (selectedTags.length > 0) {
      list = list.filter(c => (c.tags || []).some(t => selectedTags.includes(t)));
    }
    switch (sortBy) {
      case "newest":         list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")); break;
      case "duration_asc":   list.sort((a, b) => (a.duration_hours || 0) - (b.duration_hours || 0)); break;
      case "duration_desc":  list.sort((a, b) => (b.duration_hours || 0) - (a.duration_hours || 0)); break;
      case "price_asc":      list.sort((a, b) => (a.price_npr || 0) - (b.price_npr || 0)); break;
      case "price_desc":     list.sort((a, b) => (b.price_npr || 0) - (a.price_npr || 0)); break;
      case "popular":
      default:               list.sort((a, b) => (b.enrolled_count || 0) - (a.enrolled_count || 0)); break;
    }
    return list;
  }, [tab, items, mine, enrolledIds, selectedTags, sortBy]);

  // Group by difficulty when on browse tab with no tag filter
  const grouped = useMemo(() => {
    if (tab !== "browse" || selectedTags.length > 0 || level !== "All") return null;
    const buckets = { Beginner: [], Intermediate: [], Advanced: [], Other: [] };
    visibleList.forEach(c => (buckets[c.level] || buckets.Other).push(c));
    return Object.entries(buckets).filter(([_, arr]) => arr.length > 0);
  }, [tab, selectedTags, level, visibleList]);

  const toggleTag = (t) => setSelectedTags(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);

  return (
    <div data-testid="courses-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">LEARNING (LMS)</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
            Research & AI <em className="italic text-kb-primary">literacy</em> courses.
          </h1>
        </div>
        {isAuthor && (
          <Link to="/dashboard/courses/new/edit" className="kb-btn-primary text-sm" data-testid="author-new-course-btn">
            <Plus className="w-3.5 h-3.5" /> Author new course
          </Link>
        )}
      </div>

      {isAuthor && (
        <div className="mt-6 inline-flex border border-slate-200 rounded-sm bg-white p-1" data-testid="courses-tabs">
          <button onClick={() => setSearchParams({ tab: "browse" })}
            className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm ${tab === "browse" ? "bg-kb-primary text-white" : "text-kb-ink-muted"}`}
            data-testid="courses-tab-browse">
            <BookOpenCheck className="w-3.5 h-3.5 inline mr-1.5" /> Browse all
          </button>
          <button onClick={() => setSearchParams({ tab: "mine" })}
            className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm ${tab === "mine" ? "bg-kb-primary text-white" : "text-kb-ink-muted"}`}
            data-testid="courses-tab-mine">
            <Wand2 className="w-3.5 h-3.5 inline mr-1.5" /> My courses ({mine.length})
          </button>
        </div>
      )}

      {/* Continue learning section (only for browse tab and only if user has enrollments) */}
      {tab === "browse" && enrolledCourses.length > 0 && (
        <section className="mt-8" data-testid="continue-learning-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">CONTINUE LEARNING</div>
              <h2 className="font-serif text-2xl text-kb-ink mt-1">Pick up where you left off</h2>
            </div>
            <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{enrolledCourses.length} enrolled</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {enrolledCourses.map((c) => {
              const enr = c._enrollment;
              const done = (enr?.progress || 0) >= 100;
              return (
                <Link key={c.id} to={done ? `/dashboard/courses/${c.id}/certificate` : `/dashboard/courses/${c.id}/learn`}
                  className="kb-card p-5 group hover:border-kb-primary transition-colors" data-testid={`enrolled-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-2 py-1 rounded-sm">
                      {c.level}
                    </span>
                    {done && <Award className="w-4 h-4 text-amber-500" />}
                  </div>
                  <div className="font-serif text-lg text-kb-ink mt-3 leading-tight line-clamp-2">{c.title}</div>
                  <div className="mt-4">
                    <div className="flex items-center justify-between kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                      <span>PROGRESS</span>
                      <span className="font-mono text-kb-primary">{enr?.progress || 0}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full ${done ? "bg-emerald-500" : "bg-kb-primary"}`} style={{ width: `${enr?.progress || 0}%` }} />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-200 inline-flex items-center gap-1.5 kb-mono text-[10px] uppercase tracking-wider text-kb-primary group-hover:gap-2 transition-all">
                    {done ? (<><Award className="w-3 h-3" /> View certificate</>) : (<><Play className="w-3 h-3" fill="currentColor" /> Continue</>)} <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {tab === "browse" && (
        <div className="mt-8 space-y-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <input className="kb-input flex-1" placeholder="Search courses…" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="courses-search-input" />
            <div className="flex gap-2 flex-wrap">
              {LEVELS.map((l) => (
                <button key={l} onClick={() => setLevel(l)}
                  className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border transition-colors ${
                    level === l ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted"
                  }`}
                  data-testid={`courses-level-${l.toLowerCase()}`}
                >{l}</button>
              ))}
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border border-slate-200 bg-white text-kb-ink"
                data-testid="courses-sort-select">
                {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {allTags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="courses-tag-filter">
              <Filter className="w-3.5 h-3.5 text-kb-ink-muted" />
              <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">TAGS:</span>
              {allTags.map(([t, count]) => {
                const active = selectedTags.includes(t);
                return (
                  <button key={t} onClick={() => toggleTag(t)}
                    className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors inline-flex items-center gap-1 ${
                      active ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted hover:border-kb-primary/50"
                    }`}
                    data-testid={`tag-${t}`}>
                    {t} <span className={active ? "text-white/70" : "text-kb-ink-light"}>·{count}</span>
                  </button>
                );
              })}
              {selectedTags.length > 0 && (
                <button onClick={() => setSelectedTags([])} className="kb-mono text-[10px] uppercase tracking-wider text-rose-600 hover:text-rose-700 inline-flex items-center gap-1" data-testid="clear-tags">
                  <X className="w-3 h-3" /> Clear tags
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading courses…</div>
      ) : visibleList.length === 0 ? (
        <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted" data-testid="courses-empty">
          {tab === "mine" ? "You haven't authored a course yet. Click 'Author new course' to start."
            : selectedTags.length > 0 ? "No courses match the selected tags." : "No courses available."}
        </div>
      ) : grouped && grouped.length > 1 ? (
        // Grouped by difficulty
        <div className="mt-8 space-y-10">
          {grouped.map(([groupLevel, list]) => (
            <section key={groupLevel} data-testid={`group-${groupLevel.toLowerCase()}`}>
              <div className="flex items-baseline gap-3 mb-4">
                <h3 className="font-serif text-xl text-kb-ink">{groupLevel}</h3>
                <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{list.length} course{list.length === 1 ? "" : "s"}</span>
              </div>
              <CourseGrid list={list} tab={tab} />
            </section>
          ))}
        </div>
      ) : (
        <div className="mt-8">
          <CourseGrid list={visibleList} tab={tab} />
        </div>
      )}
    </div>
  );
}

function CourseGrid({ list, tab }) {
  const { user } = useAuth();
  const fetchAll = () => window.location.reload();
  const fetchMine = () => {};
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in">
      {list.map((c) => (
        <div key={c.id} className="relative group">
          {user?.role === "admin" && (
            <button
              onClick={async (e) => {
                e.preventDefault(); e.stopPropagation();
                if (!confirm(`Delete course "${c.title}"? This is permanent.`)) return;
                try {
                  await api.delete(`/courses/${c.id}`);
                  toast.success("Course deleted");
                  fetchAll(); fetchMine();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Delete failed");
                }
              }}
              className="absolute top-2 right-2 z-10 p-1.5 bg-white/95 border border-rose-200 text-rose-600 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-50"
              data-testid={`admin-course-${c.id}-delete`}
              title="Delete (admin)"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <Link to={tab === "mine" ? `/dashboard/courses/${c.id}/edit` : `/dashboard/courses/${c.id}`} className="kb-card p-6 block" data-testid={`course-${c.id}`}>
          {c.cover_image_url && (
            <div className="-m-6 mb-4 h-32 bg-slate-100 overflow-hidden rounded-t-sm">
              <img src={c.cover_image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex items-start justify-between">
            <GraduationCap className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
            <div className="flex gap-1.5">
              {c.status === "draft" && tab === "mine" && (
                <span className="kb-mono text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm">DRAFT</span>
              )}
              <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-2 py-1 rounded-sm">
                {c.level}
              </span>
            </div>
          </div>
          <div className="font-serif text-xl text-kb-ink mt-5 leading-tight">{c.title}</div>
          <div className="text-sm text-kb-ink-muted mt-2 line-clamp-2">{c.description}</div>
          {c.tags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {c.tags.slice(0, 3).map(t => (
                <span key={t} className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted bg-slate-100 px-1.5 py-0.5 rounded-sm">{t}</span>
              ))}
            </div>
          )}
          <div className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-between text-xs">
            <div className="flex items-center gap-3 text-kb-ink-muted">
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /><span className="kb-mono">{c.duration_hours}h</span></span>
              <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /><span className="kb-mono">{c.enrolled_count || 0}</span></span>
            </div>
            {c.price_npr > 0 ? (
              <span className="kb-mono text-kb-primary">NPR {c.price_npr.toLocaleString()}</span>
            ) : (
              <span className="kb-mono text-emerald-700">FREE</span>
            )}
          </div>
          </Link>
        </div>
      ))}
    </div>
  );
}
